import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type {
  Lead,
  Stage,
  CustomField,
  CreateLeadData,
  UpdateLeadData,
  LeadFilters,
  DashboardStats,
  GetLeadsResponse,
  GetLeadResponse,
  CreateLeadResponse,
  UpdateLeadResponse,
  DeleteLeadResponse,
  BulkDeleteLeadsResponse,
  GetStagesResponse,
  GetCustomFieldsResponse,
  GetDashboardStatsResponse,
  BulkUploadResponse,
  // CRM types
  Task,
  GetTasksResponse,
  GetTaskResponse,
  CreateTaskData,
  CreateTaskResponse,
  UpdateTaskProgressData,
  UpdateTaskProgressResponse,
  AddTaskNoteData,
  AddTaskNoteResponse,
  Appointment,
  GetAppointmentsResponse,
  GetAppointmentResponse,
  CreateAppointmentData,
  CreateAppointmentResponse,
  UpdateAppointmentStatusData,
  UpdateAppointmentStatusResponse,
  CallLog,
  GetCallsResponse,
  GetCallResponse,
  CreateCallLogData,
  CreateCallLogResponse,
  Ticket,
  GetTicketsResponse,
  GetTicketResponse,
  CreateTicketData,
  CreateTicketResponse,
  UpdateTicketData,
  UpdateTicketResponse,
  AddTicketNoteData,
  AddTicketNoteResponse,
} from '@varkwave/core/types';

// Import dashboard types
import type {
  DashboardFilters,
  LeadsDashboard,
  AttendanceDashboard,
  AdmissionsDashboard,
  HRDashboard,
  AnalyticsDashboard
} from '@varkwave/core';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:4000';

// ============================================================================
// All types are imported from @varkwave/core/types (source of truth)
// ============================================================================

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      // Custom params serializer to handle nested objects like customFields
      paramsSerializer: {
        serialize: (params) => {
          const searchParams = new URLSearchParams();
          
          Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null) {
              return;
            }
            
            // Handle nested objects (like customFields)
            if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
              Object.entries(value as Record<string, any>).forEach(([nestedKey, nestedValue]) => {
                if (nestedValue !== undefined && nestedValue !== null) {
                  searchParams.append(`${key}[${nestedKey}]`, String(nestedValue));
                }
              });
            } else if (Array.isArray(value)) {
              // Handle arrays
              value.forEach((item) => {
                searchParams.append(key, String(item));
              });
            } else {
              // Handle primitive values
              searchParams.append(key, String(value));
            }
          });
          
          return searchParams.toString();
        }
      }
    });

    // Request interceptor to add auth token and tenant selection
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add selected tenant for ORG_HEAD users
        const selectedTenantId = localStorage.getItem('selectedTenantId');
        if (selectedTenantId) {
          config.headers['X-Selected-Tenant'] = selectedTenantId;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear auth data but don't redirect here to avoid loops
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('currentTenantId');
          // Let the auth store handle the redirect through React Router
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  // Regular user login (requires organization ID - tenant auto-determined)
  async login(credentials: { email: string; password: string; organizationId: string }) {
    const response = await this.client.post('/auth/login', credentials);
    return response.data;
  }

  // System user login (SUPER_ADMIN, ORG_HEAD - no tenant required)
  async systemLogin(credentials: { identifier: string; password: string }) {
    const response = await this.client.post('/auth/system-login', credentials);
    return response.data;
  }

  // Get accessible tenants for current user (ORG_HEAD can see all, others see their own)
  async getAccessibleTenants() {
    const response = await this.client.get('/api/tenant-filter/accessible-tenants');
    return response.data;
  }
  
  // Set current tenant context (for ORG_HEAD filtering)
  async setTenantContext(tenantId: string | null) {
    const response = await this.client.post('/api/tenant-filter/context', { tenantId });
    
    // Store selected tenant in localStorage for automatic header inclusion
    if (tenantId) {
      localStorage.setItem('selectedTenantId', tenantId);
    } else {
      localStorage.removeItem('selectedTenantId');
    }
    
    return response.data;
  }

  // Set "all tenants" mode for ORG_HEAD
  async setAllTenantsMode() {
    localStorage.removeItem('selectedTenantId'); // No header = all tenants
    return { success: true, message: 'Viewing all tenants in organization' };
  }

  // Helper method to get currently selected tenant
  getSelectedTenantId(): string | null {
    return localStorage.getItem('selectedTenantId');
  }

  // Helper method to clear tenant selection
  clearTenantSelection() {
    localStorage.removeItem('selectedTenantId');
  }

  async register(userData: any) {
    const response = await this.client.post('/auth/register', userData);
    return response.data;
  }

  // User endpoints
  async getProfile() {
    const response = await this.client.get('/profile');
    return response.data;
  }

  async updateProfile(data: any) {
    const response = await this.client.patch('/profile', data);
    return response.data;
  }

  async changePassword(data: { currentPassword: string; newPassword: string }) {
    const response = await this.client.patch('/profile/password', data);
    return response.data;
  }

  // Organization endpoints
  async getOrganizations() {
    const response = await this.client.get('/auth/organizations');
    return response.data;
  }

  async getOrganization(id: string) {
    const response = await this.client.get(`/api/orgs/${id}`);
    return response.data;
  }

  async getAllOrganizations() {
    const response = await this.client.get('/api/orgs');
    return response.data;
  }

  async createOrganization(orgData: any) {
    const response = await this.client.post('/api/orgs', orgData);
    return response.data;
  }

  async updateOrganization(id: string, orgData: any) {
    const response = await this.client.patch(`/api/orgs/${id}`, orgData);
    return response.data;
  }

  async deleteOrganization(id: string) {
    const response = await this.client.delete(`/api/orgs/${id}`);
    return response.data;
  }

  // Tenant endpoints - now use middleware context instead of URL params
  async getTenants() {
    const response = await this.client.get('/api/orgs/tenants');
    return response.data;
  }

  async getUsers() {
    const response = await this.client.get('/api/orgs/users');
    return response.data;
  }

  async getTeamUsers() {
    const response = await this.client.get('/api/orgs/team-users');
    return response.data;
  }

  async createUser(userData: any) {
    const response = await this.client.post('/api/orgs/users', userData);
    return response.data;
  }

  async updateUser(userId: string, userData: any) {
    const response = await this.client.patch(`/api/orgs/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: string) {
    const response = await this.client.delete(`/api/orgs/users/${userId}`);
    return response.data;
  }

  async resetUserPassword(userId: string, newPassword: string) {
    const response = await this.client.patch(`/api/orgs/users/${userId}/reset-password`, { newPassword });
    return response.data;
  }

  async seedDefaultStages() {
    const response = await this.client.post('/api/orgs/seed-stages');
    return response.data;
  }

  // ============================================================================
  // LEAD MANAGEMENT ENDPOINTS
  // ============================================================================

  // Lead CRUD operations
  async createLead(data: CreateLeadData): Promise<{ success: boolean; lead: Lead; message: string }> {
    const response = await this.client.post('/api/leads', data);
    return response.data;
  }

  async getLeads(filters: LeadFilters = {}): Promise<GetLeadsResponse> {
    const response = await this.client.get('/api/leads', { params: filters });
    return response.data;
  }

  async getLeadById(id: string): Promise<{ success: boolean; lead: Lead }> {
    const response = await this.client.get(`/api/leads/${id}`);
    return response.data;
  }

  async updateLead(id: string, data: UpdateLeadData): Promise<{ success: boolean; lead: Lead; message: string }> {
    const response = await this.client.put(`/api/leads/${id}`, data);
    return response.data;
  }

  async deleteLead(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/api/leads/${id}`);
    return response.data;
  }

  async bulkDeleteLeads(leadIds: string[]): Promise<{ success: boolean; message: string; deletedCount: number }> {
    const response = await this.client.post('/api/leads/bulk-delete', { leadIds });
    return response.data;
  }

  async bulkUploadLeads(file: File): Promise<{ 
    success: boolean; 
    message: string; 
    results: {
      total: number;
      success: number;
      failed: number;
      errors: Array<{ 
        rowNumber: number; 
        data: Record<string, any>; 
        error: string; 
      }>;
    }
  }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.client.post('/api/leads/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getBulkUploadTemplate(): Promise<Blob> {
    const response = await this.client.get('/api/leads/bulk-upload/template', {
      responseType: 'blob'
    });
    return response.data;
  }

  async bulkAssignLeads(assignmentData: {
    leadIds: string[];
    assignmentType: 'specific' | 'random' | 'equal' | 'balanced';
    assignToUserId?: string;
    assignToUserIds?: string[];
    distribution?: Array<{ user: any; count: number }>;
  }): Promise<{ 
    success: boolean; 
    error?: string; 
    assignedCount?: number;
  }> {
    const response = await this.client.post('/api/leads/bulk-assign', assignmentData);
    return response.data;
  }

  async getOrganizationUsers(filters?: {
    roles?: string[];
    includeLeadCounts?: boolean;
  }): Promise<{
    success: boolean;
    users?: Array<{
      id: string;
      username: string;
      email: string;
      userProfile?: {
        fullName: string;
      };
      currentLeadCount?: number;
    }>;
    error?: string;
  }> {
    const response = await this.client.get('/api/orgs/users', { 
      params: filters 
    });
    return response.data;
  }

  async getConfigurationLockStatus(): Promise<{
    success: boolean;
    data: {
      isLocked: boolean;
      lockedAt: string | null;
      lockedBy: string | null;
      reason: string | null;
      canModifyConfiguration: boolean;
      leadsExist: boolean;
      userRole: string;
    };
  }> {
    const response = await this.client.get('/api/leads/config/lock-status');
    return response.data;
  }

  // Stage management
  async getStages(): Promise<GetStagesResponse> {
    const response = await this.client.get('/api/leads/config/stages');
    return response.data;
  }

  async createStage(data: { name: string; position?: number }): Promise<{ success: boolean; stage: Stage; message: string }> {
    const response = await this.client.post('/api/leads/config/stages', data);
    return response.data;
  }

  async updateStage(id: string, data: { name: string }): Promise<{ success: boolean; stage: Stage; message: string }> {
    const response = await this.client.put(`/api/leads/config/stages/${id}`, data);
    return response.data;
  }

  async deleteStage(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/api/leads/config/stages/${id}`);
    return response.data;
  }

  // Custom fields management
  async getCustomFields(): Promise<GetCustomFieldsResponse> {
    const response = await this.client.get('/api/leads/config/attributes');
    return response.data;
  }

  async createCustomField(data: {
    fieldName: string;
    fieldType: 'text' | 'number' | 'email' | 'phone' | 'select' | 'textarea' | 'date';
    required?: boolean;
    options?: string[];
  }): Promise<{ success: boolean; customField: CustomField; message: string }> {
    const response = await this.client.post('/api/leads/config/attributes', data);
    return response.data;
  }

  async deleteCustomField(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete(`/api/leads/config/attributes/${id}`);
    return response.data;
  }

  // Source options management
  async getSourceOptions(): Promise<{ success: boolean; sourceOptions: string[] }> {
    const response = await this.client.get('/api/leads/config/source-options');
    return response.data;
  }

  async updateSourceOptions(sourceOptions: string[]): Promise<{ success: boolean; sourceOptions: string[]; message: string }> {
    const response = await this.client.put('/api/leads/config/source-options', { sourceOptions });
    return response.data;
  }

  // Task management for leads
  async createLeadTask(leadId: string, taskData: {
    title: string;
    description?: string;
    dueDate?: string;
    priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  }) {
    const response = await this.client.post(`/api/leads/${leadId}/tasks`, taskData);
    return response.data;
  }

  async getLeadTasks(leadId: string) {
    const response = await this.client.get(`/api/leads/${leadId}/tasks`);
    return response.data;
  }

  async updateTaskStatus(taskId: string, status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED', notes?: string) {
    const response = await this.client.put(`/api/leads/tasks/${taskId}/status`, { status, notes });
    return response.data;
  }

  // Call log management
  async createCallLog(leadId: string, callData: {
    duration?: number;
    notes?: string;
    status?: 'completed' | 'missed' | 'voicemail';
  }) {
    const response = await this.client.post(`/api/leads/${leadId}/call-logs`, callData);
    return response.data;
  }

  async getLeadCallLogs(leadId: string) {
    const response = await this.client.get(`/api/leads/${leadId}/call-logs`);
    return response.data;
  }

  // Lead actions (status change with task/appointment creation)
  async updateLeadStatus(leadId: string, statusData: {
    newStatus: string;
    notes?: string;
    createTask?: boolean;
    taskTitle?: string;
    taskDescription?: string;
    taskDueDate?: string;
    taskPriority?: 'HIGH' | 'MEDIUM' | 'LOW';
    scheduleAppointment?: boolean;
    appointmentTitle?: string;
    appointmentDescription?: string;
    appointmentStartTime?: string;
    appointmentEndTime?: string;
    appointmentLocation?: string;
  }) {
    const response = await this.client.put(`/api/leads/${leadId}/status`, statusData);
    return response.data;
  }

        // Dashboard statistics
        async getLeadDashboardStats(period: string = '7d'): Promise<{ success: boolean; stats: DashboardStats }> {
          const response = await this.client.get(`/api/leads/dashboard/stats?period=${period}`);
          return response.data;
        }

        // Get sales executives for lead assignment
        async getSalesExecutives(): Promise<{ success: boolean; salesExecutives: Array<{id: string; username: string; email: string; userProfile?: {fullName: string}}> }> {
          const response = await this.client.get('/api/leads/sales-executives');
          return response.data;
        }

        // General task management
        async getAllTasks(params?: { page?: number; limit?: number; status?: string; priority?: string; search?: string }): Promise<GetTasksResponse> {
          const response = await this.client.get('/api/leads/tasks', { params });
          return response.data;
        }

        async getTaskStats(params?: { status?: string; priority?: string; assignedToId?: string }): Promise<{ success: boolean; stats: any }> {
          const response = await this.client.get('/api/leads/tasks/stats', { params });
          return response.data;
        }

        async updateTaskProgress(taskId: string, action: 'START' | 'COMPLETE' | 'PAUSE', notes?: string): Promise<UpdateTaskProgressResponse> {
          const response = await this.client.put(`/api/leads/tasks/${taskId}/progress`, { action, notes });
          return response.data;
        }

        async addTaskNote(taskId: string, content: string): Promise<AddTaskNoteResponse> {
          const response = await this.client.post(`/api/leads/tasks/${taskId}/notes`, { content });
          return response.data;
        }

        // Lead appointments
        async getLeadAppointments(leadId: string): Promise<GetAppointmentsResponse> {
          const response = await this.client.get('/api/appointments', { 
            params: { leadId, limit: 100 } 
          });
          return response.data;
        }

        // Appointment management
        async getAllAppointments(filters?: {
          status?: string;
          assignedToId?: string;
          search?: string;
          leadId?: string;
          from?: string;
          to?: string;
          page?: number;
          limit?: number;
        }): Promise<GetAppointmentsResponse> {
          const response = await this.client.get('/api/appointments', { params: filters });
          return response.data;
        }

        async getAppointmentStats(params?: { assignedToId?: string }): Promise<{ success: boolean; stats: any }> {
          const response = await this.client.get('/api/appointments/stats', { params });
          return response.data;
        }

        async createAppointment(appointmentData: {
          title: string;
          description?: string;
          startTime: string;
          endTime: string;
          location?: string;
          meetingType?: 'IN_PERSON' | 'VIRTUAL' | 'PHONE';
          meetingUrl?: string;
          leadId?: string;
          assignedToId?: string;
        }) {
          // Transform frontend data to match backend API
          const backendData = {
            title: appointmentData.title,
            description: appointmentData.description,
            scheduledAt: appointmentData.startTime,
            duration: appointmentData.endTime && appointmentData.startTime 
              ? Math.round((new Date(appointmentData.endTime).getTime() - new Date(appointmentData.startTime).getTime()) / (1000 * 60))
              : 60,
            location: appointmentData.location,
            meetingType: appointmentData.meetingType || 'IN_PERSON',
            meetingUrl: appointmentData.meetingUrl,
            leadId: appointmentData.leadId,
            assignedToId: appointmentData.assignedToId
          };
          
          const response = await this.client.post('/api/appointments', backendData);
          return response.data;
        }

        async getAppointment(appointmentId: string): Promise<GetAppointmentResponse> {
          const response = await this.client.get(`/api/appointments/${appointmentId}`);
          return {
            success: true,
            appointment: response.data.data
          };
        }

        async updateAppointmentStatus(appointmentId: string, status: 'scheduled' | 'completed' | 'cancelled' | 'no_show'): Promise<UpdateAppointmentStatusResponse> {
          const response = await this.client.patch(`/api/appointments/${appointmentId}/status`, { status });
          return response.data;
        }

        async updateAppointment(appointmentId: string, appointmentData: any): Promise<any> {
          const response = await this.client.patch(`/api/appointments/${appointmentId}`, appointmentData);
          return response.data;
        }

  // Tasks endpoints
  async getTasks(params?: any) {
    const response = await this.client.get('/api/tasks', { params });
    return response.data;
  }

  async getTask(taskId: string) {
    const response = await this.client.get(`/api/tasks/${taskId}`);
    return response.data;
  }

  async createTask(taskData: any) {
    const response = await this.client.post('/api/tasks', taskData);
    return response.data;
  }

  async updateTask(taskId: string, taskData: any) {
    const response = await this.client.patch(`/api/tasks/${taskId}`, taskData);
    return response.data;
  }

  async completeTask(taskId: string, data?: any) {
    const response = await this.client.patch(`/api/tasks/${taskId}/complete`, data);
    return response.data;
  }

  // Appointments endpoints
  async getAppointments(params?: any) {
    const response = await this.client.get('/api/appointments', { params });
    return response.data;
  }


  // Attendance endpoints
  async getAttendance(params?: any) {
    const response = await this.client.get('/api/attendance', { params });
    return response.data;
  }

  async markAttendance(attendanceData: any) {
    const response = await this.client.post('/api/attendance', attendanceData);
    return response.data;
  }

  async updateAttendance(attendanceId: string, attendanceData: any) {
    const response = await this.client.patch(`/api/attendance/${attendanceId}`, attendanceData);
    return response.data;
  }

  // Leave endpoints
  async getLeaves(params?: any) {
    const response = await this.client.get('/api/hr/leaves', { params });
    return response.data;
  }

  async createLeave(leaveData: any) {
    const response = await this.client.post('/api/hr/leaves', leaveData);
    return response.data;
  }

  async updateLeave(leaveId: string, leaveData: any) {
    const response = await this.client.patch(`/api/hr/leaves/${leaveId}`, leaveData);
    return response.data;
  }

  async approveLeave(leaveId: string, data?: any) {
    const response = await this.client.patch(`/api/hr/leaves/${leaveId}/approve`, data);
    return response.data;
  }

  async rejectLeave(leaveId: string, data?: any) {
    const response = await this.client.patch(`/api/hr/leaves/${leaveId}/reject`, data);
    return response.data;
  }

  // School endpoints
  async getSchools() {
    const response = await this.client.get('/api/schools');
    return response.data;
  }

  async createSchool(schoolData: any) {
    const response = await this.client.post('/api/schools', schoolData);
    return response.data;
  }

  async updateSchool(schoolId: string, schoolData: any) {
    const response = await this.client.patch(`/api/schools/${schoolId}`, schoolData);
    return response.data;
  }

  // Students endpoints
  async getStudents(params?: any) {
    const response = await this.client.get('/api/students', { params });
    return response.data;
  }

  async createStudent(studentData: any) {
    const response = await this.client.post('/api/students', studentData);
    return response.data;
  }

  async updateStudent(studentId: string, studentData: any) {
    const response = await this.client.patch(`/api/students/${studentId}`, studentData);
    return response.data;
  }

  // Classes endpoints
  async getClasses() {
    const response = await this.client.get('/api/schools/classes');
    return response.data;
  }

  async createClass(classData: any) {
    const response = await this.client.post('/api/schools/classes', classData);
    return response.data;
  }

  // HR endpoints
  async getEmployees(params?: any) {
    const response = await this.client.get('/api/hr/employees', { params });
    return response.data;
  }

  async createEmployee(employeeData: any) {
    const response = await this.client.post('/api/hr/employees', employeeData);
    return response.data;
  }

  async updateEmployee(employeeId: string, employeeData: any) {
    const response = await this.client.patch(`/api/hr/employees/${employeeId}`, employeeData);
    return response.data;
  }

  // Payroll endpoints
  async getPayrolls(params?: any) {
    const response = await this.client.get('/api/hr/payrolls', { params });
    return response.data;
  }

  async createPayroll(payrollData: any) {
    const response = await this.client.post('/api/hr/payrolls', payrollData);
    return response.data;
  }

  // Analytics endpoints
  async getDashboardStats() {
    const response = await this.client.get('/api/analytics/dashboard');
    return response.data;
  }

  async getReports(type: string, params?: any) {
    const response = await this.client.get(`/api/analytics/reports/${type}`, { params });
    return response.data;
  }

  // ============================================================================
  // STUDENT MANAGEMENT SYSTEM API METHODS
  // ============================================================================

  // Student Management
  async createStudentRecord(studentData: any) {
    const response = await this.client.post('/api/student-management/students', studentData);
    return response.data;
  }

  async getStudentsList(params?: {
    page?: number;
    limit?: number;
    search?: string;
    schoolId?: string;
    classId?: string;
    currentGrade?: string;
    academicYear?: string;
    isActive?: boolean;
  }) {
    const response = await this.client.get('/api/student-management/students', { params });
    return response.data;
  }

  async getStudentById(studentId: string) {
    const response = await this.client.get(`/api/student-management/students/${studentId}`);
    return response.data;
  }

  async updateStudentRecord(studentId: string, studentData: any) {
    const response = await this.client.put(`/api/student-management/students/${studentId}`, studentData);
    return response.data;
  }

  async deleteStudentRecord(studentId: string) {
    const response = await this.client.delete(`/api/student-management/students/${studentId}`);
    return response.data;
  }

  // Bulk Student Operations
  async uploadStudentsBulk(csvContent: string, defaultSchoolId?: string) {
    const response = await this.client.post('/api/student-management/students/bulk-upload', {
      csvContent,
      defaultSchoolId
    });
    return response.data;
  }

  async getStudentUploadTemplate() {
    const response = await this.client.get('/api/student-management/students/bulk-upload/template', {
      responseType: 'blob'
    });
    return response.data;
  }

  async validateStudentBulkData(csvContent: string, defaultSchoolId?: string) {
    const response = await this.client.post('/api/student-management/students/bulk-upload/validate', {
      csvContent,
      defaultSchoolId
    });
    return response.data;
  }

  async getStudentsByGrade(schoolId: string, grade: string, academicYear: string) {
    const response = await this.client.get('/api/student-management/students/by-grade', {
      params: { schoolId, grade, academicYear }
    });
    return response.data;
  }

  // Grade Configuration Management
  async createGradeConfig(gradeData: {
    schoolId: string;
    gradeName: string;
    gradeCode: string;
    gradeOrder: number;
    description?: string;
    minAge?: number;
    maxAge?: number;
  }) {
    const response = await this.client.post('/api/student-management/grade-configs', gradeData);
    return response.data;
  }

  async getGradesBySchool(schoolId: string) {
    const response = await this.client.get(`/api/student-management/schools/${schoolId}/grade-configs`);
    return response.data;
  }

  async getGradeConfigById(gradeConfigId: string) {
    const response = await this.client.get(`/api/student-management/grade-configs/${gradeConfigId}`);
    return response.data;
  }

  async updateGradeConfig(gradeConfigId: string, gradeData: any) {
    const response = await this.client.put(`/api/student-management/grade-configs/${gradeConfigId}`, gradeData);
    return response.data;
  }

  async deleteGradeConfig(gradeConfigId: string) {
    const response = await this.client.delete(`/api/student-management/grade-configs/${gradeConfigId}`);
    return response.data;
  }

  // Fee Structure Management
  async createGradeFeeStructure(feeStructureData: {
    gradeConfigId: string;
    feeStructureId: string;
    academicYear: string;
    paymentFrequency: string;
    totalAmount: number;
    installmentAmount?: number;
    installmentCount?: number;
    paymentSchedule?: any;
  }) {
    const response = await this.client.post('/api/student-management/grade-fee-structures', feeStructureData);
    return response.data;
  }

  async getGradeFeeStructures(gradeConfigId: string, academicYear?: string) {
    const response = await this.client.get(`/api/student-management/grade-configs/${gradeConfigId}/fee-structures`, {
      params: { academicYear }
    });
    return response.data;
  }

  async updateGradeFeeStructure(feeStructureId: string, feeStructureData: any) {
    const response = await this.client.put(`/api/student-management/grade-fee-structures/${feeStructureId}`, feeStructureData);
    return response.data;
  }

  async deleteGradeFeeStructure(feeStructureId: string) {
    const response = await this.client.delete(`/api/student-management/grade-fee-structures/${feeStructureId}`);
    return response.data;
  }

  async copyFeeStructuresToNewYear(copyData: {
    gradeConfigId: string;
    fromAcademicYear: string;
    toAcademicYear: string;
    adjustmentPercentage?: number;
  }) {
    const response = await this.client.post('/api/student-management/grade-fee-structures/copy', copyData);
    return response.data;
  }

  // Academic Progression
  async getAvailablePromotionGrades(schoolId: string, currentGradeOrder: number) {
    const response = await this.client.get(`/api/student-management/schools/${schoolId}/promotion-grades`, {
      params: { currentGradeOrder }
    });
    return response.data;
  }

  // Fee Management
  async getFeeStructures(params?: any) {
    const response = await this.client.get('/api/fee-structures', { params });
    return response.data;
  }

  async createFeeStructure(feeStructureData: any) {
    const response = await this.client.post('/api/fee-structures', feeStructureData);
    return response.data;
  }

  // Admission Dashboard Stats
  async getAdmissionStats(schoolId?: string, academicYear?: string) {
    const response = await this.client.get('/api/student-management/admission-stats', {
      params: { schoolId, academicYear }
    });
    return response.data;
  }

  // Parent Notifications
  async sendFeeReminders(data: {
    studentIds?: string[];
    schoolId?: string;
    reminderType?: 'BEFORE_DUE' | 'OVERDUE' | 'FINAL_NOTICE';
  }) {
    const response = await this.client.post('/api/student-management/fee-reminders', data);
    return response.data;
  }

  // Academic Progression Jobs
  async triggerAcademicProgression(data: {
    schoolId: string;
    fromAcademicYear: string;
    toAcademicYear: string;
    dryRun?: boolean;
  }) {
    const response = await this.client.post('/api/student-management/academic-progression', data);
    return response.data;
  }

  async getProgressionJobStatus(jobId: string) {
    const response = await this.client.get(`/api/student-management/academic-progression/${jobId}`);
    return response.data;
  }

  // Job Management & Monitoring
  async getJobStatistics() {
    const response = await this.client.get('/api/student-management/jobs/stats');
    return response.data;
  }

  async getJobHealth() {
    const response = await this.client.get('/api/student-management/jobs/health');
    return response.data;
  }

  // Generic request method
  async request<T = any>(method: string, url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.request({
      method,
      url,
      data,
    });
    return response.data;
  }

  // Direct HTTP methods for convenience
  async post<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url);
    return response.data;
  }

  async get<T = any>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, { params });
    return response.data;
  }
  
  // ========================================================================
  // CHAT SYSTEM APIs
  // ========================================================================
  
  // WhatsApp Configuration
  async setupWhatsApp(config: {
    accessToken: string;
    phoneNumberId: string;
    businessAccountId: string;
    verifyToken: string;
    webhookUrl?: string;
    businessHoursStart?: string;
    businessHoursEnd?: string;
    dailyMessageLimit?: number;
    monthlyMessageLimit?: number;
  }) {
    const response = await this.client.post('/chat/whatsapp/setup', config);
    return response.data;
  }
  
  async getWhatsAppConfig() {
    const response = await this.client.get('/chat/whatsapp/config');
    return response.data;
  }
  
  async testWhatsAppConnection() {
    const response = await this.client.post('/chat/whatsapp/test');
    return response.data;
  }
  
  // ========================================================================
  // WATI APIs - REMOVED DUPLICATES
  // ========================================================================
  
  
  // Lead-specific WATI messaging
  async getLeadConversations(leadId: string) {
    const response = await this.client.get(`/api/leads/${leadId}/conversations`);
    return response.data;
  }
  
  async getLeadMessages(leadId: string, conversationId: string, params?: {
    page?: number;
    limit?: number;
  }) {
    const response = await this.client.get(`/api/leads/${leadId}/conversations/${conversationId}/messages`, { params });
    return response.data;
  }
  
  async sendLeadTemplateMessage(leadId: string, data: {
    templateId: string;
    parameters?: Record<string, string>;
  }) {
    const response = await this.client.post(`/api/leads/${leadId}/messages/template`, data);
    return response.data;
  }
  
  async sendLeadDirectMessage(leadId: string, data: {
    message: string;
  }) {
    const response = await this.client.post(`/api/leads/${leadId}/messages/direct`, data);
    return response.data;
  }
  
  // Conversations
  async getConversations(params?: {
    page?: number;
    limit?: number;
    status?: string;
    assignedTo?: string;
    search?: string;
  }) {
    const response = await this.client.get('/chat/conversations', { params });
    return response.data;
  }
  
  async getConversation(conversationId: string) {
    const response = await this.client.get(`/chat/conversations/${conversationId}`);
    return response.data;
  }
  
  async updateConversation(conversationId: string, data: {
    status?: string;
    assignedToId?: string;
    priority?: string;
    tags?: string[];
  }) {
    const response = await this.client.put(`/chat/conversations/${conversationId}`, data);
    return response.data;
  }
  
  // Messages
  async sendMessage(data: {
    to: string;
    message: string;
    conversationId?: string;
  }) {
    const response = await this.client.post('/chat/messages/send', data);
    return response.data;
  }
  
  async sendMediaMessage(data: {
    to: string;
    mediaUrl: string;
    mediaType: 'image' | 'document' | 'audio' | 'video';
    caption?: string;
    filename?: string;
  }) {
    const response = await this.client.post('/chat/messages/send-media', data);
    return response.data;
  }
  
  // Chat Flows and Rules
  async getChatFlows() {
    const response = await this.client.get('/chat/flows');
    return response.data;
  }
  
  async createChatFlow(data: {
    name: string;
    description?: string;
    priority?: number;
    triggerStages?: string[];
    businessHoursOnly?: boolean;
  }) {
    const response = await this.client.post('/chat/flows', data);
    return response.data;
  }
  
  async updateChatFlow(flowId: string, data: Partial<{
    name: string;
    description: string;
    priority: number;
    triggerStages: string[];
    businessHoursOnly: boolean;
    isActive: boolean;
  }>) {
    const response = await this.client.put(`/chat/flows/${flowId}`, data);
    return response.data;
  }
  
  async deleteChatFlow(flowId: string) {
    const response = await this.client.delete(`/chat/flows/${flowId}`);
    return response.data;
  }
  
  async createChatRule(data: {
    flowId: string;
    name: string;
    description?: string;
    order?: number;
    triggerType: string;
    triggerConfig: Record<string, any>;
    conditions?: Array<{
      field: string;
      condition: string;
      value: string;
      isRequired?: boolean;
      logicalOperator?: string;
      groupId?: string;
    }>;
    actions: Array<{
      type: string;
      config: Record<string, any>;
      order?: number;
      condition?: string;
    }>;
  }) {
    const response = await this.client.post('/chat/rules', data);
    return response.data;
  }
  
  async testChatRule(ruleId: string, testContext: any) {
    const response = await this.client.post(`/chat/rules/${ruleId}/test`, { testContext });
    return response.data;
  }
  
  // Message Templates
  async getMessageTemplates(params?: {
    provider?: string;
    category?: string;
  }) {
    const response = await this.client.get('/chat/templates', { params });
    return response.data;
  }
  
  async createMessageTemplate(data: {
    name: string;
    content: string;
    mediaUrl?: string;
    mediaType?: string;
    provider?: string;
    language?: string;
    category?: string;
    variables?: string[];
  }) {
    const response = await this.client.post('/chat/templates', data);
    return response.data;
  }
  
  // ============================================================================
  // DASHBOARD API METHODS
  // ============================================================================
  
  // Get dashboard data for specific dashboard type
  async getDashboardData(
    dashboardType: 'leads' | 'attendance' | 'admissions' | 'hr' | 'analytics',
    params?: Partial<DashboardFilters & { userId?: string; teamId?: string; department?: string; grade?: string }>
  ) {
    const response = await this.client.get(`/api/dashboard/${dashboardType}`, { params });
    return response.data;
  }
  
  // Get leads dashboard data
  async getLeadsDashboard(params?: Partial<DashboardFilters & { userId?: string }>) {
    const response = await this.client.get('/api/dashboard/leads', { params });
    return response.data;
  }
  
  // Get attendance dashboard data
  async getAttendanceDashboard(params?: Partial<DashboardFilters & { department?: string }>) {
    const response = await this.client.get('/api/dashboard/attendance', { params });
    return response.data;
  }
  
  // Get admissions dashboard data
  async getAdmissionsDashboard(params?: Partial<DashboardFilters & { grade?: string }>) {
    const response = await this.client.get('/api/dashboard/admissions', { params });
    return response.data;
  }
  
  // Get HR dashboard data
  async getHRDashboard(params?: Partial<DashboardFilters>) {
    const response = await this.client.get('/api/dashboard/hr', { params });
    return response.data;
  }
  
  // Get comprehensive analytics dashboard data
  async getAnalyticsDashboard(params?: Partial<DashboardFilters & { userId?: string; teamId?: string }>) {
    const response = await this.client.get('/api/dashboard/analytics', { params });
    return response.data;
  }

  // Get sales performance reports
  async getSalesPerformanceReports(params?: { period?: string; startDate?: string; endDate?: string }) {
    const response = await this.client.get('/api/reports/sales-performance', { params });
    return response.data;
  }
  
  async getTodaysPulse(params?: { userId?: string }) {
    const response = await this.client.get('/api/dashboard/todays-pulse', { params });
    return response.data;
  }
  
  // Get dashboard overview (for determining accessible dashboards)
  async getDashboardOverview() {
    const response = await this.client.get('/api/dashboard/overview');
    return response.data;
  }
  
  // Get user activity analytics
  async getUserActivityAnalytics(params?: Partial<DashboardFilters & { userId?: string }>) {
    const response = await this.client.get('/api/dashboard/analytics/user-activity', { params });
    return response.data;
  }
  
  // Get performance analytics
  async getPerformanceAnalytics(params?: Partial<DashboardFilters & { userId?: string; teamId?: string }>) {
    const response = await this.client.get('/api/dashboard/analytics/performance', { params });
    return response.data;
  }
  
  // Get detailed lead analytics
  async getLeadAnalytics(params?: Partial<DashboardFilters & { userId?: string }>) {
    const response = await this.client.get('/api/dashboard/analytics/leads', { params });
    return response.data;
  }
  
  // Get task analytics
  async getTaskAnalytics(params?: Partial<DashboardFilters>) {
    const response = await this.client.get('/api/dashboard/analytics/tasks', { params });
    return response.data;
  }
  
  // Get appointment analytics
  async getAppointmentAnalytics(params?: Partial<DashboardFilters>) {
    const response = await this.client.get('/api/dashboard/analytics/appointments', { params });
    return response.data;
  }

  // Get sales team members
  async getSalesTeamMembers() {
    const response = await this.client.get('/api/dashboard/sales-team');
    return response.data;
  }

  // Chat Analytics
  async getChatAnalytics(params?: {
    period?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const response = await this.client.get('/chat/analytics', { params });
    return response.data;
  }

  // ==========================================================================
  // SYSTEM USER MANAGEMENT METHODS (SUPER_ADMIN ONLY)
  // ==========================================================================

  // Organization Management
  async getSystemOrganizations() {
    const response = await this.client.get('/api/system-user/organizations');
    return response.data.data;
  }

  async getSystemOrganizationDetails(orgId: string) {
    const response = await this.client.get(`/api/system-user/organizations/${orgId}`);
    return response.data;
  }

  async deleteSystemOrganization(orgId: string) {
    const response = await this.client.delete(`/api/orgs/${orgId}`);
    return response.data;
  }

  // Tenant Management  
  async getOrganizationTenants(orgId: string) {
    const response = await this.client.get(`/api/system-user/organizations/${orgId}/tenants`);
    return response.data.data;
  }

  async createTenant(orgId: string, data: {
    name: string;
    type: 'SCHOOL' | 'SALES_COMPANY' | 'HYBRID';
  }) {
    const response = await this.client.post(`/api/system-user/organizations/${orgId}/tenants`, data);
    return response.data.tenant;
  }

  async getTenantDetails(tenantId: string) {
    const response = await this.client.get(`/api/system-user/tenants/${tenantId}`);
    return response.data;
  }

  async updateTenant(tenantId: string, data: {
    name?: string;
    type?: 'SCHOOL' | 'SALES_COMPANY' | 'HYBRID';
    isActive?: boolean;
  }) {
    const response = await this.client.put(`/api/system-user/tenants/${tenantId}`, data);
    return response.data.tenant;
  }

  async deactivateTenant(tenantId: string) {
    const response = await this.client.delete(`/api/system-user/tenants/${tenantId}`);
    return response.data.tenant;
  }

  // User Management
  async getTenantUsers(tenantId: string) {
    const response = await this.client.get(`/api/system-user/tenants/${tenantId}/users`);
    return response.data.data;
  }

  async createTenantUser(tenantId: string, data: {
    name: string;
    username: string;
    email: string;
    password: string;
    role: string;
    phone?: string;
  }) {
    const response = await this.client.post(`/api/system-user/tenants/${tenantId}/users`, data);
    return response.data.user;
  }

  async updateSystemUser(userId: string, data: {
    name?: string;
    email?: string;
    phone?: string;
    isActive?: boolean;
    role?: string;
  }) {
    const response = await this.client.put(`/api/system-user/users/${userId}`, data);
    return response.data.user;
  }

  async deactivateSystemUser(userId: string) {
    const response = await this.client.delete(`/api/system-user/users/${userId}`);
    return response.data.user;
  }

  async getAvailableRoles() {
    const response = await this.client.get('/api/system-user/roles');
    return response.data.data;
  }

  // System User Management
  async getAllSystemUsers() {
    const response = await this.client.get('/api/system-user/system-users');
    return response.data.systemUsers;
  }

  async createSystemUserAccount(data: {
    name: string;
    username: string;
    email: string;
    password: string;
    organizationId: string;
    isSuperAdmin?: boolean;
  }) {
    const response = await this.client.post('/api/system-user/system-users', data);
    return response.data.systemUser;
  }

  // Organization-Level User Management (Super Admin)
  async getSystemOrganizationUsers(orgId: string) {
    const response = await this.client.get(`/api/system-user/organizations/${orgId}/users`);
    return response.data.data;
  }

  async createOrganizationUser(orgId: string, data: {
    name: string;
    username: string;
    email: string;
    password: string;
  }) {
    const response = await this.client.post(`/api/system-user/organizations/${orgId}/users`, data);
    return response.data.user;
  }
  
  // ==========================================================================
  // WATI INTEGRATION METHODS
  // ==========================================================================
  
  // WATI Configuration
  async setupWati(data: {
    watiApiEndpoint: string;
    bearerToken?: string;
    businessPhone?: string;
    businessName?: string;
    businessAccountId?: string;
    verifyToken?: string;
    autoResponseEnabled?: boolean;
    businessHoursStart?: string;
    businessHoursEnd?: string;
    timezone?: string;
    dailyMessageLimit?: number;
    monthlyMessageLimit?: number;
  }) {
    const response = await this.client.post('/api/wati/config', data);
    return response.data;
  }

  async getWatiConfig() {
    const response = await this.client.get('/api/wati/config');
    // Backend returns { success, config, webhookUrl } - extract the config
    return response.data.config;
  }

  async testWatiConnection() {
    const response = await this.client.post('/api/wati/config/test');
    return response.data;
  }

  async validateWatiForBulkMessaging() {
    const response = await this.client.get('/api/wati/config/validate-bulk');
    return response.data;
  }
  
  // WATI Templates
  async syncWatiTemplates() {
    const response = await this.client.post('/api/wati/templates/sync');
    return response.data;
  }

  async getWatiTemplates(filters?: {
    category?: string;
    status?: string;
    search?: string;
  }) {
    const response = await this.client.get('/api/wati/templates', { params: filters });
    return response.data;
  }

  async getWatiTemplate(templateId: string) {
    const response = await this.client.get(`/api/wati/templates/${templateId}`);
    return response.data;
  }

  // WATI Messaging
  async sendWatiTemplateMessage(data: {
    templateId: string;
    to: string;
    parameters?: Record<string, string>;
    leadId?: string;
  }) {
    const response = await this.client.post('/api/wati/templates/send', {
      templateId: data.templateId,
      to: data.to,
      parameters: data.parameters || {},
      leadId: data.leadId
    });
    return response.data;
  }

  async sendWatiDirectMessage(data: {
    phone: string;
    message: string;
  }) {
    const response = await this.client.post('/api/wati/messages/send', {
      to: data.phone,  // Convert 'phone' to 'to' for backend
      message: data.message
    });
    return response.data;
  }

  async sendBulkTemplateMessages(data: {
    template_name: string;
    broadcast_name: string;
    receivers: Array<{
      whatsappNumber: string;
      customParams: Array<{
        name: string;
        value: string;
      }>;
    }>;
    channel_number: string;
  }) {
    const response = await this.client.post('/api/wati/templates/sendBulk', data);
    return response.data;
  }
  
  // WATI Contacts
  async addWatiContact(data: {
    name: string;
    phone: string;
    customParams?: Record<string, any>;
  }) {
    const response = await this.client.post('/api/wati/contacts', data);
    return response.data;
  }
  
  async getWatiContacts() {
    const response = await this.client.get('/api/wati/contacts');
    return response.data;
  }

  async getWatiMessages(filters?: {
    phone?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await this.client.get('/api/wati/messages', { params: filters });
    return response.data;
  }

  async getWatiLeadMessages(leadPhone: string, leadName?: string) {
    const response = await this.client.get('/api/wati/messages/lead', {
      params: { phone: leadPhone, name: leadName }
    });
    return response.data;
  }
  
  // WATI Analytics
  async getWatiTemplateUsage() {
    const response = await this.client.get('/api/wati/analytics/usage');
    return response.data;
  }
}

export const apiClient = new ApiClient();
export default apiClient;

// ============================================================================
// CHAT SYSTEM TYPES
// ============================================================================

export interface ChatConversation {
  id: string;
  tenantId: string;
  leadId: string;
  provider: 'WHATSAPP' | 'SMS' | 'EMAIL' | 'WEB_CHAT';
  externalId?: string;
  customerPhone: string;
  customerName?: string;
  status: 'ACTIVE' | 'WAITING' | 'RESOLVED' | 'ARCHIVED';
  assignedToId?: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  tags: string[];
  lastMessageAt?: Date;
  firstMessageAt?: Date;
  isUnread: boolean;
  unreadCount: number;
  messageCount: number;
  avgResponseTime?: number;
  satisfactionScore?: number;
  createdAt: Date;
  updatedAt: Date;
  lead?: Lead;
  assignedTo?: {
    id: string;
    name: string;
    email: string;
  };
  messages?: ChatMessage[];
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  externalId?: string;
  type: 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'AUDIO' | 'VIDEO' | 'LOCATION' | 'CONTACT';
  content: string;
  mediaUrl?: string;
  mediaType?: string;
  mediaSize?: number;
  isInbound: boolean;
  status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  statusUpdatedAt?: Date;
  senderPhone?: string;
  senderName?: string;
  userId?: string;
  isAutomated: boolean;
  ruleExecutionId?: string;
  sentAt?: Date;
  deliveredAt?: Date;
  readAt?: Date;
  createdAt: Date;
  user?: {
    id: string;
    name: string;
  };
}

export interface ChatFlow {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  isActive: boolean;
  priority: number;
  triggerStages: string[];
  businessHoursOnly: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdById: string;
  createdBy: {
    id: string;
    name: string;
  };
  rules?: ChatRule[];
}

export interface ChatRule {
  id: string;
  flowId: string;
  name: string;
  description?: string;
  isActive: boolean;
  order: number;
  triggerType: string;
  triggerConfig: Record<string, any>;
  executionCount: number;
  successCount: number;
  lastExecutedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  conditions: ChatRuleCondition[];
  actions: ChatRuleAction[];
}

export interface ChatRuleCondition {
  id: string;
  ruleId: string;
  field: string;
  condition: string;
  value: string;
  isRequired: boolean;
  logicalOperator: string;
  groupId?: string;
  createdAt: Date;
}

export interface ChatRuleAction {
  id: string;
  ruleId: string;
  type: string;
  config: Record<string, any>;
  order: number;
  condition?: string;
  createdAt: Date;
}

export interface MessageTemplate {
  id: string;
  tenantId: string;
  name: string;
  content: string;
  mediaUrl?: string;
  mediaType?: string;
  provider: string;
  language: string;
  category?: string;
  usageCount: number;
  lastUsedAt?: Date;
  variables: string[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdById: string;
  createdBy: {
    id: string;
    name: string;
  };
}

export interface WhatsAppConfig {
  id: string;
  phoneNumberId: string;
  businessAccountId: string;
  webhookUrl?: string;
  isActive: boolean;
  isVerified: boolean;
  autoResponseEnabled: boolean;
  businessHoursStart?: string;
  businessHoursEnd?: string;
  timezone: string;
  dailyMessageLimit?: number;
  monthlyMessageLimit?: number;
  createdAt: Date;
  updatedAt: Date;
}

// Export types for use in components
export type { 
  Lead, 
  Stage, 
  CustomField, 
  CreateLeadData, 
  UpdateLeadData, 
  LeadFilters, 
  DashboardStats
};
